import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { View } from 'react-native';
import { Colors, Flex, IconChevronRight, Typography } from '@banhala/ui-kit';
import Animated, {
  cancelAnimation,
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { useRelatedLiveCampaign } from '../../queries/useRelatedLiveCampaign';
import { usePostParticipateLiveCampaign } from '../../queries/usePostParticipateLiveCampaign';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useIsAnonymousUser } from 'shared/src/hooks/useIsAnonymousUser';
import { useOpenDeeplink } from 'shared/src/hooks';
import { bridge, isNowBetween } from 'shared/src/utils';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';
import type { MarketLiveCampaign } from 'shared/src/api/goodsService/getRelatedLiveCampaign/types';

const LiveIndicator = () => {
  const opacity = useSharedValue(1);

  useEffect(() => {
    opacity.value = withRepeat(
      withTiming(0.3, { duration: 800, easing: Easing.inOut(Easing.ease) }),
      -1,
      true,
    );
    return () => cancelAnimation(opacity);
  }, [opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  return (
    <Flex.Center width={24} height={24}>
      <Animated.View
        style={[
          {
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: '#EC0000',
          },
          animatedStyle,
        ]}
      />
    </Flex.Center>
  );
};

const getIsOnAir = (campaign: MarketLiveCampaign): boolean => {
  return isNowBetween(campaign.started_at, campaign.ended_at);
};

export const LiveCampaignBanner: React.FC = () => {
  const { data: campaign } = useRelatedLiveCampaign();
  const isBGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['B'],
  });

  if (!campaign || !isBGroup) {
    return null;
  }

  return <LiveCampaignBannerContent campaign={campaign} />;
};

const LiveCampaignBannerContent: React.FC<{
  campaign: MarketLiveCampaign;
}> = ({ campaign }) => {
  const isAnonymous = useIsAnonymousUser();
  const { mutate: participate } = usePostParticipateLiveCampaign();
  const logEvent = useLogGoodsAnalyticsEvent();
  const { openDeeplink } = useOpenDeeplink();
  const hasLoggedView = useRef(false);

  const isOnAir = useMemo(() => getIsOnAir(campaign), [campaign]);

  useEffect(() => {
    if (hasLoggedView.current) return;
    hasLoggedView.current = true;

    logEvent({
      name: 'VIEW_SHOPLIVE_BANNER',
      analytics: {
        ...campaign.logging.analytics,
        IS_ON_AIR: isOnAir ? '1' : '0',
      },
    });
    logEvent({
      name: 'VIEW_SHOPLIVE_TARGET_GOODS_DETAIL',
      channel: ['braze'],
      analytics: campaign.logging.analytics,
    });
  }, [campaign, isOnAir, logEvent]);

  const handlePressBanner = useCallback(() => {
    logEvent({
      name: 'CLICK_SHOPLIVE_BANNER',
      analytics: {
        ...campaign.logging.analytics,
        IS_ON_AIR: isOnAir ? '1' : '0',
      },
    });

    const deeplink = isOnAir
      ? campaign.player_deeplink
      : campaign.event_deeplink;
    openDeeplink(deeplink);
  }, [campaign, isOnAir, logEvent, openDeeplink]);

  const handlePressSubscribe = useCallback(() => {
    logEvent({
      name: 'CLICK_REQUEST_SHOPLIVE_NOTIFICATION',
      analytics: campaign.logging.analytics,
    });

    if (isAnonymous) {
      bridge.assertLogin({});
      return;
    }

    participate(campaign.event_sno);
  }, [campaign, isAnonymous, logEvent, participate]);

  return (
    <Flex
      backgroundColor={'bg_secondary'}
      borderBottomWidth={1}
      borderColor={'border_primary'}
    >
      <Flex.Row
        flex={1}
        alignItems={'center'}
        padding={{ horizontal: 16, vertical: 8 }}
        gap={8}
      >
        <Flex.Row
          flex={1}
          alignItems={'center'}
          gap={4}
          onPress={handlePressBanner}
        >
          {isOnAir ? (
            <LiveIndicator />
          ) : (
            <View
              style={{
                backgroundColor: '#EC0000',
                borderRadius: 4,
                paddingHorizontal: 4,
                paddingVertical: 2,
              }}
            >
              <Typography type={'subtitle4'} color={'content_always_white'}>
                LIVE
              </Typography>
            </View>
          )}

          <Typography
            type={'subtitle3'}
            color={'content_primary'}
            numberOfLines={1}
          >
            {campaign.market_name}
          </Typography>

          <Flex flex={1}>
            <Typography
              type={'body3'}
              color={'content_secondary'}
              numberOfLines={1}
            >
              {campaign.title}
            </Typography>
          </Flex>

          <IconChevronRight fill={Colors.content_tertiary} size={16} />
        </Flex.Row>

        {!isOnAir && (
          <Flex
            onPress={campaign.is_subscribed ? undefined : handlePressSubscribe}
            borderRadius={4}
            borderWidth={1}
            borderColor={'border_primary'}
            padding={{ horizontal: 8, vertical: 4 }}
            opacity={campaign.is_subscribed ? 0.5 : 1}
          >
            <Typography
              type={'body4'}
              color={
                campaign.is_subscribed ? 'content_tertiary' : 'content_primary'
              }
            >
              {campaign.is_subscribed ? '알림받기 완료' : '알림받기'}
            </Typography>
          </Flex>
        )}
      </Flex.Row>
    </Flex>
  );
};
